import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Grid2, InputAdornment, Typography } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import { addDays, isSameDay } from "date-fns";
import format from "date-fns/format";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useDateFnsLocale } from "../../../../utils/date-locale";
import useShiftDemandData from "../../controllers/use-shift-demand-data";
const BORDER_COLOR = "#E5E5E5";

// Constants for layout
const SIDEBAR_WIDTH = "210px"; // Width for the sidebar (if any)
const MAIN_CONTENT_FLEX = 1; // Remaining space will be divided equally

interface DemandCalendarHeaderProps {
	searchText: string;
	setSearchText: (text: string) => void;
	currentDate: string;
	startingDay: Date;
}

export const DemandCalendarHeader: React.FC<DemandCalendarHeaderProps> = ({
	searchText,
	setSearchText,
	currentDate,
	startingDay,
}) => {
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();

	return (
		<Grid2
			container
			width={"100%"}
			sx={{
				flexGrow: 1,
			}}
		>
			{/* Day headers */}
			<Grid2
				container
				width={"100%"}
				sx={{
					position: "sticky",
					top: 0,
					height: "64px",
					minHeight: "64px",
					backgroundColor: "rgba(255,255,255)",
					boxShadow: "0px 2px 4px -2px rgba(0, 0, 0, 0.2)",
					flexWrap: "nowrap",
					zIndex: 1000,
				}}
			>
				{/* Sidebar space reservation */}

				<Box
					sx={{
						width: SIDEBAR_WIDTH,
						borderRight: `1px solid ${BORDER_COLOR}`,
						borderLeft: `1px solid ${BORDER_COLOR}`,
						borderTop: `1px solid ${BORDER_COLOR}`,
						// borderBottom: `1px solid ${BORDER_COLOR}`,
						height: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<InputBase
						startAdornment={
							<InputAdornment position='start'>
								<SearchIcon />
							</InputAdornment>
						}
						endAdornment={
							searchText &&
							searchText?.length > 0 && (
								<InputAdornment
									position='end'
									onClick={() => setSearchText?.("")}
									sx={{
										cursor: "pointer",
									}}
								>
									<CloseIcon />
								</InputAdornment>
							)
						}
						placeholder={t("common_search")}
						value={searchText}
						onChange={(e) => setSearchText?.(e.target.value)}
						sx={{
							width: "100%",
							height: "100%",
							padding: "0 16px",
							border: "none",
							outline: "none",
						}}
					/>
				</Box>

				<Box
					sx={{
						width: SIDEBAR_WIDTH,
						borderRight: `1px solid ${BORDER_COLOR}`,
						borderLeft: `1px solid ${BORDER_COLOR}`,
						borderTop: `1px solid ${BORDER_COLOR}`,
						// borderBottom: `1px solid ${BORDER_COLOR}`,
						height: "100%",
						display: "flex",
						justifyContent: "flex-start",
						alignItems: "flex-start",
						paddingLeft: "4px",
						paddingTop: "2px",
					}}
				>
					<Typography
						sx={{
							fontSize: "16px",
							lineHeight: "28px",
							fontWeight: 400,
							fontFamily: "Roboto",
							color: "rgba(0,0,0,0.6)",
							marginRight: "0.5rem",
						}}
					>
						{t("shift-demand_shift-pattern")}
					</Typography>
				</Box>
				{/* Days container */}
				<Box
					sx={{
						display: "flex",
						flex: MAIN_CONTENT_FLEX,
						height: "100%",
					}}
				>
					{currentDate &&
						Array.from({ length: 7 }).map((_, index) => {
							const day = addDays(startingDay, index);
							return (
								<React.Fragment key={index}>
									<Box
										sx={{
											display: "flex",
											flex: 1,
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											textAlign: "center",
											height: "100%",
											position: "relative",
											paddingLeft: "0.8rem",
											borderRight: `1px solid ${BORDER_COLOR}`,
											borderTop: `1px solid ${BORDER_COLOR}`,
											//	borderBottom: `1px solid ${BORDER_COLOR}`,
											backgroundColor: isSameDay(day, new Date())
												? "rgba(47, 77, 139, 0.04)"
												: "",
											// width: "172px",
										}}
									>
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												justifyContent: "center",
												alignItems: "flex-start",
											}}
										>
											<Typography
												sx={{
													fontSize: "16px",
													lineHeight: "28px",
													fontWeight: 400,
													fontFamily: "Roboto",
													color: isSameDay(day, new Date())
														? "#2F4D8B"
														: "rgba(0,0,0,0.6)",
													marginRight: "0.5rem",
												}}
											>
												{format(day, "EEE", { locale: dateLocale })}
											</Typography>
											<Typography
												sx={{
													fontSize: "24px",
													lineHeight: "32px",
													fontWeight: 400,
													fontFamily: "Roboto",
													color: isSameDay(day, new Date())
														? "#2F4D8B"
														: "rgba(0,0,0,0.6)",
												}}
											>
												{format(day, "d")}
											</Typography>
										</Box>
									</Box>
								</React.Fragment>
							);
						})}
				</Box>
			</Grid2>
		</Grid2>
	);
};
